/**
 * Copyright (c) 2018-present, Didi, Inc.
 * All rights reserved.
 *
 * @author Cory(kuanghongrui@didichuxing.com)
 *
 * @file The class of AOMGH5PageInitPlugin
 */

import { ITrackedEvent } from 'com/didichuxing/tracker/event/ITrackedEvent';
import { ITrackedEventArgs } from 'com/didichuxing/tracker/plugin/IReportingStrategyPlugin';
import OMGH5PerformanceEvent from 'com/didichuxing/tracker/h5/plugins/events/OMGH5PerformanceEvent';
import AReportingStrategyPlugin from 'com/didichuxing/tracker/plugin/AReportingStrategyPlugin';
import PluginUtils from 'com/didichuxing/tracker/plugin/utils/PluginUtils';

export default abstract class AOMGH5PerformancePlugin extends AReportingStrategyPlugin {
    /**
     * 是否上报过
     */
   private isReport: boolean = false;

    /**
     * 获取全局性能信息。
     * {@link https://github.com/addyosmani/timing.js/issues/9}
     * @returns {Performance}
     */
    private globalPerformance(): Performance {
        const global: any = window;
        return global.performance || global.webkitPerformance || global.msPerformance || global.mozPerformance;
    }

    /**
     * 获取请求的时间统计信息
     */
    private getEntries(): any[] {
        const performance: Performance = this.globalPerformance();
        if (performance && performance.getEntries) {
            const entries: any[] = window.performance.getEntries() || [];
            if (entries.length > 100) {
                entries.length = 100;
            }
            return entries;
        } else {
            return [];
        }
    }

    private getEntryLength(): number {
        const performance: Performance = this.globalPerformance();
        if (performance && performance.getEntries) {
            const entries: any[] = window.performance.getEntries() || [];
            return entries.length;
        } else {
            return 0;
        }
    }

    private reportData() {
      if (this.isReport) {
        return;
      }
      document.removeEventListener('visibilitychange', this.onVisibilityChange, true);

      const entries = this.getEntries();
      const entryLength = this.getEntryLength();
      const timing: PerformanceTiming = performance.timing;
      this.report({
          eventId: PluginUtils.EVENT_ID_PERFORMANCE,
          attrs: {
              entries: entries,
              entryLength: entryLength,
              pageLoadTime: timing.loadEventStart - timing.navigationStart
          }
      });
      this.isReport = true;
    }

    private onVisibilityChange() {
      const performance: Performance = this.globalPerformance();
      if (document.visibilityState === 'hidden') {
        performance && performance.mark('hidden');
      }
      if (document.visibilityState === 'visible') {
        performance && performance.mark('visible');
        document.removeEventListener('visibilitychange', this.onVisibilityChange, true);
      }
    }

    /**
     * @override
     * @returns {string}
     */
    public getName(): string {
        return PluginUtils.PLUGIN_NAME_PERFORMANCE;
    }

    /**
     * @override
     * @inheritDoc
     */
    public execute(): void {
      this.onVisibilityChange = this.onVisibilityChange.bind(this);
      document.addEventListener('visibilitychange', this.onVisibilityChange, true);

      const performance: Performance = this.globalPerformance();
      try {
        const performanceObserver: PerformanceObserver = new PerformanceObserver(() => {
          performance && performance.mark('largest-contentful-paint');
        });
        performanceObserver.observe({ entryTypes: ['largest-contentful-paint'] });
        // 如果有用户交互,PerformanceObserver会停止观测,此时上报性能数据
        ['keydown', 'click'].forEach((type) => {
          addEventListener(type, () => {
            this.reportData();
            if (performanceObserver && performanceObserver.disconnect) {
              performanceObserver.disconnect();
            }
          }, { once: true, capture: true });
        });

      } catch (e) {
        // 如果浏览器不支持此API
        this.reportData();
      }
    }

    /**
     * @override
     * @inheritDoc
     */
    public exit(): void {
        /* Nothing to exit */
    }

    /**
     * @override
     * @inheritDoc
     * @param {ITrackedEventArgs} eventArgs
     * @returns {ITrackedEvent}
     */
    protected generateEvent(eventArgs: ITrackedEventArgs): ITrackedEvent {
        return new OMGH5PerformanceEvent(eventArgs.attrs, this.getContext());
    }
}
